/**
 * /use-cases/pm-roadmap — concrete walkthrough of using Draftlize for
 * PM roadmap planning. Heavier on the specific workflow than /for-pm
 * (which is the funnel landing).
 */
import { MarketingShell } from '../MarketingShell';

export function PmRoadmap() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/use-cases/pm-roadmap"
      documentTitle="Use case: PM roadmap planning · Draftlize"
      documentDescription="How PMs run quarterly roadmap planning in Draftlize. From initial brainstorm to decisions to dependent specs — a concrete walkthrough of the substrate-first PM workflow."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Use case · PM roadmap planning</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            How PMs plan roadmap<br/>
            <em>without losing the rationale.</em>
          </h1>
          <p className="lede">
            A concrete walkthrough: quarterly roadmap planning from blank page to shippable specs, with AI agent doing half the work and decisions surviving across the whole cycle.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Week 1 · <em>What's the substrate?</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Start with intent</h3>
            <p>Open Draftlize, create a new project ("Q3 roadmap"). Write a thread: "I want to plan Q3 around three themes: retention, monetization, and onboarding velocity. Help me think through which to weight most."</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Agent extracts cards</h3>
            <p>As you go back and forth, agent calls <code>write_card</code> on key thoughts: <code>c-01 statement: retention is the leading indicator</code>, <code>c-02 question: should monetization wait for retention?</code>, etc. You see them land in the cards sidebar live.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Decide</h3>
            <p>By end of week 1 you have ~30 cards. Agent helps you promote the key ones to <code>decision</code> kind: <code>c-12 decision: prioritize retention in Q3, monetization in Q4</code>. This is now addressable and citable forever.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Week 4 · <em>The decision changes.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">User research surfaces a surprise</h3>
            <p>You learn that monetization is actually the constraint, not retention. You edit <code>c-12</code>: "prioritize monetization in Q3, retention in Q4."</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Substrate flags everything stale</h3>
            <p>Stale propagation walks backward through <code>→depends-on c-12</code>. 17 cards get flagged: retention OKRs, sprint plans, hiring asks, marketing copy drafts. You see strikethrough + amber bars.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Update or delete, deliberately</h3>
            <p>Walk through each flagged card. Some are still valid (just need re-asserting). Some need rewriting. A few get deleted. By Friday the substrate is consistent again — instead of a quarter of misaligned docs you discover at demo day.</p>
          </div>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Week 8 · <em>Agent reads your past decisions automatically.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">New chat, no re-explaining</h3>
            <p>You open a new thread to design the monetization upgrade flow. Agent reads <code>c-12</code> (priority decision), <code>c-19</code> (pricing strategy), <code>c-22</code> (target persona) as ambient context — and starts from "given monetization is the Q3 focus and we're targeting mid-market, here are 3 upgrade flow patterns to consider."</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Cross-thread conflict detection</h3>
            <p>You suggested "$30/mo enterprise" in one thread. Last week you committed to "$60 Power tier" in another. Draftlize files this as a <code>gap_kind=conflict</code> card with both source threads linked. You catch it before it ships.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Specs ready to graduate to Linear</h3>
            <p>When a card is concrete enough to ship, export to Linear (markdown copy, or paste the card URL). Mark the card <code>built</code> when shipped. Edit it later → status auto-flips to <code>drift_detected</code>.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          By week 12 you've made ~80 decisions, ~200 statements, ~50 questions resolved. All addressable. All linked. All readable by your AI agent next quarter when you plan Q4.
        </blockquote>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Try it for a <em>sprint.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Your free $5 covers a sprint's worth of thinking — enough to feel one decision reversal with stale propagation. That's usually when PMs realize this isn't just better notes.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
